import { Vector3 } from "three";
import {
  CONFIGURATOR_DATA,
  CONFIGURATOR_MODEL_TYPE,
  MODEL_PART_TYPE,
} from "../configurator/ConfiguratorData";
import {
  alignJoints,
  fetchMinDistance,
  getPartTypeMatch,
  getPossibleBindableJoints,
  realignJoints,
} from "../util/JointUtil";
import { disposeObject3DDeep } from "../util/GeometryUtil";
import { logger } from "../util/Logger";
import { SugarModel } from "./SugarModel";
import { SugarModelGroup } from "./SugarModelGroup";
import { SugarModelJoint } from "./SugarModelJoint";
import { createMaterial } from "./SugarModelLoader";

type ConfiguratorEvent = {
  baseJoint: SugarModelJoint;
  tailJoint: SugarModelJoint;
  operations?: any[];
  models: SugarModel[];
};

export class SugarModelConfigurator extends SugarModel {
  private _models: SugarModel[] = [];
  private _sideModels: SugarModel[] = [];

  private _unbindedJoints: SugarModelJoint[] = [];
  private _sideConfig: Map<string, MODEL_PART_TYPE> = new Map();
  private _mainConfig: Map<string, MODEL_PART_TYPE> = new Map();

  private _events: ConfiguratorEvent[] = [];

  get materialConfig() {
    return this._materialConfig;
  }

  get mainConfig() {
    return this._mainConfig;
  }

  get sideConfig() {
    return this._sideConfig;
  }

  get sideModels() {
    return this._sideModels;
  }

  private _materialConfig: Map<string, { code: string; material: any }> =
    new Map();

  private _configData?: CONFIGURATOR_MODEL_TYPE;

  get configData(): CONFIGURATOR_MODEL_TYPE {
    return this._configData ?? CONFIGURATOR_DATA;
  }

  constructor(
    mainConfig: Map<string, MODEL_PART_TYPE>,
    sideConfig: Map<string, MODEL_PART_TYPE>,
    materialConfig: Map<string, { code: string; material: any }>,
    configData?: CONFIGURATOR_MODEL_TYPE,
  ) {
    super({});
    this.name = "Configurator";
    this._mainConfig = mainConfig;
    this._sideConfig = sideConfig;
    this._materialConfig = materialConfig;
    this._configData = configData;
  }

  groupConnectedSugarModels(
    models: SugarModel[],
    events: ConfiguratorEvent[],
  ): SugarModel[][] {
    const idToModel = new Map<number, SugarModel>();
    const adjacency = new Map<number, Set<number>>();

    for (const model of models) {
      idToModel.set(model.id, model);
      adjacency.set(model.id, new Set());
    }

    for (const event of events) {
      const a = event.baseJoint.model.id;
      const b = event.tailJoint.model.id;

      adjacency.get(a)?.add(b);
      adjacency.get(b)?.add(a);
    }

    const visited = new Set<number>();
    const groups: SugarModel[][] = [];

    for (const model of models) {
      if (visited.has(model.id)) continue;

      const group: SugarModel[] = [];
      const stack = [model.id];

      while (stack.length > 0) {
        const currentId = stack.pop()!;
        if (visited.has(currentId)) continue;

        visited.add(currentId);
        const currentModel = idToModel.get(currentId);
        if (currentModel) group.push(currentModel);

        for (const neighborId of adjacency.get(currentId) ?? []) {
          if (!visited.has(neighborId)) {
            stack.push(neighborId);
          }
        }
      }

      groups.push(group);
    }

    return groups;
  }

  get products() {
    const groupedModels = this.groupConnectedSugarModels(
      this._models,
      this._events,
    );

    const result = [
      ...groupedModels.map((models) =>
        models
          .map((model) => [
            model,
            ...(model.joints
              ?.filter((j) => j.freeze && j.bindedJoint && j.visibility)
              .map((j) => j.bindedJoint?.model) || []),
          ])
          .flat(),
      ),
      this._sideModels,
      ...this._events.map((e) => e.models),
      //this._legModels || [],
    ];

    return result;
  }

  get models() {
    return this._models;
  }

  get joints() {
    return this._models.flatMap((model) => model.joints || []);
  }

  private _mergeHappened:
    | {
        distance: number;
        base: any;
        tail: any;
      }
    | undefined;

  private _merging = false;

  addModel(addModel: SugarModel) {
    this.addModelWithoutComplete(addModel);
    this.complete();
  }

  addModelWithoutComplete(addModel: SugarModel, alignDistance: number = 1) {
    this._mergeHappened = fetchMinDistance(addModel, [this]);
    const minModel = this._mergeHappened;
    if (minModel && minModel.distance < alignDistance) {
      alignJoints(minModel!.base, minModel!.tail);
      minModel.base.bindJoint(minModel.tail);
    }

    if (
      minModel &&
      minModel.distance < 1 &&
      addModel.userData.info.replaceType == 1
    ) {
      minModel.base.model.attach(addModel);
    } else {
      this.attach(addModel);
    }

    this.__addModel(addModel);
  }

  async completeSync() {
    //console.log("Configurator complete", this._dirty, this);
    if (!this._dirty) return;
    this._dirty = false;

    await this.mergePossibleJoints();
    await Promise.all([
      this.changeMainPart(this._mainConfig),
      this.changeSidePart(this._sideConfig),
      (async () => {
        if (this._mergeHappened && !this._merging) {
          this._merging = true;
          await this.runMergeRules(
            this._mergeHappened?.base,
            this._mergeHappened?.tail,
          );
          this._mergeHappened = undefined;
          this._merging = false;
        }
      })(),
    ]);
    (async (e) => {
      this.reposition();
      await Promise.all([
        this.applyMaterial(this._materialConfig, ...this._sideModels),
        this.applyMaterial(this._materialConfig, ...this._models),
        this.applyMaterial(
          this._materialConfig,
          ...this._events.map((e) => e.models).flat(),
        ),
      ]);

      this.dispatchEvent({ type: "configured" as any });
    })();
  }

  complete() {
    //console.log("Configurator complete", this._dirty, this);
    if (!this._dirty) return;
    this._dirty = false;

    this.mergePossibleJoints().then(() => {
      Promise.all([
        this.changeMainPart(this._mainConfig),
        this.changeSidePart(this._sideConfig),
        (async () => {
          if (this._mergeHappened && !this._merging) {
            this._merging = true;
            await this.runMergeRules(
              this._mergeHappened?.base,
              this._mergeHappened?.tail,
            );
            this._mergeHappened = undefined;
            this._merging = false;
          }
        })(),
      ]).then(async (e) => {
        this.reposition();
        await Promise.all([
          this.applyMaterial(this._materialConfig, ...this._sideModels),
          this.applyMaterial(this._materialConfig, ...this._models),
          this.applyMaterial(
            this._materialConfig,
            ...this._events.map((e) => e.models).flat(),
          ),
        ]);

        this.dispatchEvent({ type: "configured" as any });
      });
    });

    //this.haveJoints();
  }

  grabModel(model: SugarModel) {
    this.havePossibleJoints(model);
    this.__removeModel(model);
    //this.possesJoints();
  }

  async runMergeRules(baseJoint: SugarModelJoint, tailJoint: SugarModelJoint) {
    const baseId = baseJoint.model.userData.info.id;
    const tailId = tailJoint.model.userData.info.id;

    const hasIt = this._events.find(
      (e) =>
        (e.baseJoint == baseJoint && e.tailJoint == tailJoint) ||
        (e.tailJoint == baseJoint && e.baseJoint == tailJoint),
    );

    if (hasIt) {
      return;
    }

    const items = [baseId, tailId].sort((a, b) => a - b);
    const event = this.configData.configurator.events?.find(
      (e) =>
        e.when.type === "merged" &&
        e.when.items.length === items.length &&
        [...e.when.items].sort().join(",") === [...items].sort().join(","),
    );

    if (event) {
      const baseType = baseJoint.outType;

      const models: SugarModel[] = [];
      const operations2 = await Promise.all(
        event.operations?.map(async (operation) => {
          if (operation.action === "visible") {
            const baseJoints = baseJoint.model.joints?.filter(
              (j) => j.tags.includes(baseType) && j.bindedJoint,
            );
            baseJoints?.forEach((j) => (j.visibility = false));

            const tailType = tailJoint.outType;
            const tailJoints = tailJoint.model.joints?.filter(
              (j) => j.tags.includes(tailType) && j.bindedJoint,
            );
            tailJoints?.forEach((j) => (j.visibility = false));

            return {
              return: undefined,
              parameters: undefined,
              operation: operation,
            };
          } else if (operation.action === "add") {
            const type = operation.parameters?.type!;
            const modelId = this._mainConfig
              .get(type)
              ?.joints?.find((j) => j.type === type)?.match;
            const modelData = this.configData.models.find(
              (m) => m.id == modelId,
            );
            const modelObject = await new SugarModel(modelData).load(
              modelData,
              {
                forceSync: true,
              },
            );

            const basePosition = new Vector3();
            baseJoint.model.getWorldPosition(basePosition);

            const tailPosition = new Vector3();
            tailJoint.model.getWorldPosition(tailPosition);

            const centerPosition = basePosition
              .clone()
              .add(tailPosition)
              .multiplyScalar(0.5);
            modelObject?.position.copy(centerPosition);

            baseJoint.model.attach(modelObject!);
            models.push(modelObject!);

            if (operation.parameters?.offset) {
              modelObject?.position.setX(
                modelObject.position.x + operation.parameters.offset[0],
              );
              modelObject?.position.setY(
                modelObject.position.y + operation.parameters.offset[1],
              );
              modelObject?.position.setZ(
                modelObject.position.z + operation.parameters.offset[2],
              );
            }

            return {
              return: modelObject,
              parameters: { base: baseJoint, tail: tailJoint },
              operation: operation,
            };
          }
        }),
      );

      const eventInstance: ConfiguratorEvent = {
        baseJoint: baseJoint,
        tailJoint: tailJoint,
        operations: operations2,
        models: models,
      };

      this._events.push(eventInstance);
    }
  }

  private reposition() {
    const models = [
      ...this._models?.filter((m) => m.userData.info.replaceType == 0),
      ...this._sideModels,
    ];
    models.forEach((m) => m.detach());

    const centerPosition = models
      .reduce(
        (position, model) =>
          position.add(
            new Vector3(model.position.x, this.position.y, model.position.z),
          ),
        new Vector3(),
      )
      .multiplyScalar(1.0 / models.length);
    this.position.copy(centerPosition);
    this.updateWorldMatrix(true, true);

    models.forEach((m) => this.attach(m));
  }

  private havePossibleJoints(model: SugarModel) {
    this._unbindedJoints = getPossibleBindableJoints(model);
    //this._unbindedJoints?.forEach((joint) => joint.unbindJoint());
  }

  private async mergePossibleJoints() {
    if (this._unbindedJoints.length > 1) {
      alignJoints(this._unbindedJoints[0], this._unbindedJoints[1]);
      this._unbindedJoints[0].bindJoint(this._unbindedJoints[1]);

      realignJoints(
        this._unbindedJoints[1].model as SugarModel,
        this._unbindedJoints[1],
      );

      await this.runMergeRules(
        this._unbindedJoints[0],
        this._unbindedJoints[1],
      );
    }

    this._unbindedJoints = [];
  }

  private _dirty = true;
  private __addModel(model: SugarModel) {
    model.configurator = this;
    if (!this._models.includes(model)) this._models.push(model);
    //this.possesJoints();
    this._dirty = true;
  }

  private __removeModel(model: SugarModel) {
    this._models = this._models.filter((m) => m != model);

    model.joints
      ?.filter((j) => j && j.bindedJoint)
      .forEach((j) => {
        this.unbindEvents(j, j.bindedJoint!);
      });

    model.joints?.forEach((j) => {
      if (
        j.model == model &&
        j.binded &&
        !j.freeze &&
        (j.inType == "sol" || j.inType == "sag")
      ) {
        j.unbindJoint();
      }
      j.model = model;
    });

    model.configurator = undefined;
    model.detach();
    this._dirty = true;
  }

  private unbindEvents(baseJoint: SugarModelJoint, tailJoint: SugarModelJoint) {
    const eventInstance = this._events.find(
      (e) =>
        (e.baseJoint == baseJoint && e.tailJoint == tailJoint) ||
        (e.tailJoint == baseJoint && e.baseJoint == tailJoint),
    );

    if (eventInstance) {
      eventInstance.operations?.forEach((operationItem) => {
        if (operationItem.operation.action === "visible") {
          const baseType = baseJoint.outType;

          const baseJoints = baseJoint.model.joints?.filter(
            (j) => j.tags.includes(baseType) && j.bindedJoint,
          );
          baseJoints?.forEach((j) => (j.visibility = true));

          const tailType = tailJoint.outType;
          const tailJoints = tailJoint.model.joints?.filter(
            (j) => j.tags.includes(tailType) && j.bindedJoint,
          );
          tailJoints?.forEach((j) => (j.visibility = true));
        } else if (operationItem.operation.action === "add") {
          //
          operationItem.return?.removeFromParent();
        }
      });
      eventInstance.operations = [];
    }

    eventInstance?.models.forEach((model) => {
      model.removeFromParent();
    });

    this._events = this._events.filter((e) => e != eventInstance);
  }

  removeModel(model: SugarModel) {
    this.havePossibleJoints(model);
    this.__removeModel(model);
    this.complete();
    this.reposition();
  }

  /**
   *
   * @param Materials
   *
   *
   */

  async applyMaterial(
    materialConfig: Map<string, { code: string; material: any }>,
    ...inputModels: SugarModel[]
  ) {
    this._materialConfig = materialConfig;
    const allPromises = Array.from(materialConfig?.values() ?? []).map(
      async (materialConf) => {
        if (!materialConf.material) {
          logger.warn("material is undefined", materialConf.code);
          return;
        }
        const material = createMaterial(materialConf.material);
        await material.load();

        const models =
          inputModels.length > 0
            ? inputModels
            : [
                ...this._models,
                ...this._sideModels,
                ...this._events.map((e) => e.models),
              ].flat();

        const codes = materialConf.code.split(";");
        models?.forEach((model) => {
          model.partMaterialGroups?.forEach((part) => {
            if (codes.includes(part.code)) {
              part.apply(model, material);
            }
          });

          model.joints?.forEach((joint) => {
            const jointModel = joint.bindedJoint?.model;
            if (jointModel && model.hasIt(jointModel!)) {
              jointModel?.partMaterialGroups?.forEach((part) => {
                if (codes.includes(part.code)) {
                  part.apply(jointModel, material);
                }
              });
            }
          });
        });
      },
    );

    return await Promise.all(allPromises);
  }

  private _getEmptyJoints() {
    return Array.from(this._sideConfig.values())
      .filter((value) => value !== undefined)
      .flatMap((value) => {
        return value.joints.flatMap((jointdata) => {
          return this._models.flatMap((model) =>
            model
              .joints!.filter(
                (joint) =>
                  joint.inType === jointdata.type &&
                  !joint.binded &&
                  !joint.model?.userData?.info?.jointInfo?.excludes?.includes(
                    jointdata.id,
                  ),
              )
              .map((joint) => {
                return {
                  joint: joint,
                  data: jointdata,
                };
              }),
          );
        });
      });
  }

  async changeSidePart(sideConfig: Map<string, MODEL_PART_TYPE>) {
    this._sideConfig = sideConfig;
    //console.log("changeSidePart", sideConfig);
    logger.info("changeSidePart", sideConfig);

    this._sideModels.forEach((part) => {
      part?.removeFromParent();
    });

    const emptyjoints = this._getEmptyJoints();

    this._sideModels = (
      await Promise.all(
        emptyjoints.map(async (emptyJointSlot) => {
          const modelConfig = this.configData.models.find(
            (model) => model.id === emptyJointSlot.data.match,
          );

          if (!modelConfig) {
            logger.warn(
              "[SugarModelConfigurator.changeSidePart] modelConfig yok",
              {
                emptyJointSlotMatch: emptyJointSlot.data.match,
                emptyJointType: emptyJointSlot.data.type,
              },
            );
            return null;
          }

          let sideModel = this._sideModels.find(
            (cachedModel) => cachedModel.data.id === modelConfig.id,
          );

          if (!sideModel) {
            sideModel = await new SugarModel(modelConfig).load(modelConfig, {
              forceSync: true,
            });
          }

          if (!sideModel) {
            logger.warn(
              "[SugarModelConfigurator.changeSidePart] sideModel yüklenemedi",
              {
                modelConfigId: modelConfig.id,
              },
            );
            return null;
          }

          const tailJoint = sideModel.joints?.find(
            (j) => j.outType == emptyJointSlot.data.type,
          );

          const jointOutTypes = sideModel.joints?.map((j) => j.outType) ?? [];

          if (!tailJoint) {
            logger.warn(
              "[SugarModelConfigurator.changeSidePart] tailJoint bulunamadı (alignJoints atlanıyor)",
              {
                beklenenOutType: emptyJointSlot.data.type,
                modelId: modelConfig.id,
                modelName: modelConfig.name,
                mevcutJointOutTypes: jointOutTypes,
                baseJoint: {
                  inType: emptyJointSlot.joint.inType,
                  outType: emptyJointSlot.joint.outType,
                },
              },
            );
            return null;
          }

          sideModel.detach();
          sideModel.removeFromParent();
          sideModel.position.set(0, 0, 0);
          sideModel.rotation.set(0, 0, 0);
          sideModel.updateWorldMatrix(true, true);
          alignJoints(emptyJointSlot.joint, tailJoint, true);
          const root = emptyJointSlot.joint.model?.root;
          if (!root) {
            logger.warn(
              "[SugarModelConfigurator.changeSidePart] base joint model.root yok",
              {
                baseInType: emptyJointSlot.joint.inType,
              },
            );
            return null;
          }
          this.attach(sideModel);
          tailJoint.model.freeze = true;

          return sideModel;
        }),
      )
    ).filter((m): m is SugarModel => m != null);
  }

  async changeMainPart(partConfig: Map<string, MODEL_PART_TYPE>) {
    const allModels = this._models;
    this._mainConfig = partConfig;

    await Promise.all([
      ...allModels
        .filter((m) => m.userData.info.replaceType === 0)
        .map(async (m) => {
          await (m as SugarModelGroup).load({
            data: m.data,
            partConfig: partConfig,
          });
        }),
      ...this._events
        .map((e) => e.models)
        .flat()
        .map(async (m) => {
          await (m as SugarModel).load(
            this.configData.models.find(
              (m) => m.id == getPartTypeMatch(partConfig.get("legIn")!, 0),
            ),
            {
              forceSync: true,
              forceAgain: true,
            },
          );
        }),
    ]);
  }

  override dispose(): void {
    for (const model of this._models) {
      model?.dispose();
      model?.removeFromParent();
    }
    this._models = [];
    for (const model of this._sideModels) {
      model?.dispose();
      model?.removeFromParent();
    }
    this._sideModels = [];
    disposeObject3DDeep(this);
    super.dispose();
  }
}
